import { Flags } from "@/classes/FeatureFlags";

/**
 * Shared configuration for report views and the dashboard card grid.
 * `routeName` identifies the Vue Router route for each report (matches
 * the names declared in router.js). `dashboardSection` controls the
 * grouping on the dashboard landing page.
 */

export const REPORT_CONFIG = {
    TASKS: {
        title: "Tasks",
        icon: "tasks",
        description: "Review shared tasks, deadlines and completion history",
        routeName: "ReportTasks",
        hasSearch: false,
        hasDateRange: true,
        dashboardSection: "operations"
    },
    MEATS: {
        featureFlag: Flags.MeatModule,
        title: "Meat intakes",
        icon: "meat",
        description: "View intake records with origin and batch details",
        routeName: "ReportMeats",
        hasSearch: false,
        hasDateRange: true,
        dashboardSection: "intake"
    },
    INGREDIENTS: {
        featureFlag: Flags.IngredientsModule,
        title: "Ingredients",
        icon: "mortar-pestle",
        description: "View intake records and current stock levels",
        routeName: "ReportIngredients",
        hasSearch: false,
        hasDateRange: true,
        dashboardSection: "intake"
    },
    PACKAGING: {
        featureFlag: Flags.PackagingModule,
        title: "Packaging",
        icon: "tape",
        description: "View intake records and current quantities",
        routeName: "ReportPackaging",
        hasSearch: false,
        hasDateRange: true,
        dashboardSection: "intake"
    },
    STOCK: {
        featureFlag: Flags.StockModule,
        title: "Stock",
        icon: "boxes",
        description: "View raw materials and inventory status",
        routeName: "ReportStock",
        hasSearch: false,
        hasDateRange: false,
        dashboardSection: "inventory"
    },
    PRODUCTS: {
        featureFlag: Flags.ProductsModule,
        title: "Products",
        icon: "burger-soda",
        description: "View finished product stock and batch info",
        routeName: "ReportProducts",
        hasSearch: false,
        hasDateRange: true,
        dashboardSection: "inventory"
    },
    SUPPLIERS: {
        featureFlag: Flags.SuppliersModule,
        title: "Suppliers",
        icon: "industry-alt",
        description: "View supplier names and locations",
        routeName: "ReportSuppliers",
        hasSearch: false,
        hasDateRange: false,
        dashboardSection: "inventory"
    },
    CHECKS: {
        featureFlag: Flags.ChecksModule,
        title: "Checks",
        icon: "clipboard-check",
        description: "Review completed, late and missed checks",
        routeName: "ReportChecks",
        hasSearch: true,
        hasDateRange: true,
        searchPlaceholder: "Search checks",
        dashboardSection: "compliance"
    },
    TEMPERATURES: {
        featureFlag: Flags.TemperatureLogging,
        title: "Temperature logs",
        icon: "temperature-high",
        description: "Review readings for each monitored unit",
        routeName: "ReportTemperatures",
        hasSearch: false,
        hasDateRange: true,
        dashboardSection: "compliance"
    },
    FOOD_RECORDS: {
        featureFlag: Flags.FoodRecords,
        title: "Food records",
        icon: "utensils",
        description: "View food item action logs with temperatures",
        routeName: "ReportFoodRecords",
        hasSearch: false,
        hasDateRange: true,
        dashboardSection: "compliance"
    },
    USER_CLOCK_EVENTS: {
        featureFlag: Flags.UserClockModule,
        title: "User clock events",
        icon: "clock",
        description: "View staff clock events and hours worked",
        routeName: "ReportUserClockEvents",
        hasSearch: false,
        hasDateRange: true,
        dashboardSection: "operations"
    },
    PRODUCTION_RUNS: {
        featureFlag: Flags.MultiRecipeProduction,
        title: "Production runs",
        icon: "industry",
        description: "View committed production runs and audit status",
        routeName: "ReportProductionRuns",
        hasSearch: true,
        hasDateRange: true,
        dashboardSection: "operations"
    },
    DISPATCHES: {
        featureFlag: Flags.DispatchModule,
        title: "Dispatches",
        icon: "shipping-fast",
        description: "View outbound product movements to customers",
        routeName: "ReportDispatches",
        hasSearch: true,
        hasDateRange: true,
        dashboardSection: "operations"
    },
    PAYROLL: {
        featureFlag: Flags.WagesModule,
        title: "Payroll (estimated)",
        icon: "money-bill-wave",
        description: "Hours worked × hourly rate, with audit trail",
        routeName: "ReportPayroll",
        hasSearch: false,
        hasDateRange: true,
        dashboardSection: "operations"
    },
    PRODUCT_TRACE: {
        featureFlag: Flags.ProductsModule,
        title: "Product trace",
        icon: "route",
        description: "Trace a product back to its source materials",
        routeName: "ReportProductTrace",
        hasSearch: false,
        hasDateRange: false,
        dashboardSection: null // not shown on dashboard; reached from product detail
    }
};

export const DASHBOARD_SECTIONS = [
    {
        id: "intake",
        title: "Intake records",
        icon: "truck-loading",
        countKeys: ["meats", "ingredients", "packaging"]
    },
    {
        id: "inventory",
        title: "Inventory and products",
        icon: "boxes",
        countKeys: ["stock", "products", "suppliers"]
    },
    {
        id: "compliance",
        title: "Compliance and safety",
        icon: "clipboard-check",
        countKeys: ["checks", "temperature_logs", "food_records"]
    },
    {
        id: "operations",
        title: "Operations",
        icon: "users",
        countKeys: ["user_clock_events", "production_runs", "dispatches"]
    }
];

/**
 * Per-report-type → count key (matches the keys returned by API.Report.Counts).
 */
export const REPORT_COUNT_KEY = {
    TASKS: "tasks",
    MEATS: "meats",
    INGREDIENTS: "ingredients",
    PACKAGING: "packaging",
    STOCK: "stock",
    PRODUCTS: "products",
    SUPPLIERS: "suppliers",
    CHECKS: "checks",
    TEMPERATURES: "temperature_logs",
    FOOD_RECORDS: "food_records",
    USER_CLOCK_EVENTS: "user_clock_events",
    PRODUCTION_RUNS: "production_runs",
    DISPATCHES: "dispatches",
    // Payroll uses the same underlying dataset as User Clock Events.
    PAYROLL: "user_clock_events"
};

/**
 * Report access shared by the reports dashboard and global command palette.
 * Staff can review shared Tasks. Other reports retain administrator/auditor
 * access, mirrored by the child-route guards in router.js.
 */
export function canAccessReport(reportType, { has, role, planScoped = false }) {
    if (reportType === "TASKS") return role("admin") || role("user");
    if (!role("admin") && !role("auditor")) return false;

    if (reportType === "PAYROLL") {
        return role("admin") && has(Flags.WagesModule);
    }
    if (reportType === "PRODUCTION_RUNS") {
        return has(Flags.MultiRecipeProduction);
    }
    if (reportType === "DISPATCHES") {
        return has(Flags.DispatchModule);
    }
    const config = REPORT_CONFIG[reportType];
    return !!config && (!planScoped || has(config.featureFlag));
}

export function canAccessReportRoute(routeName, ctx) {
    const entry = Object.entries(REPORT_CONFIG).find(
        ([, config]) => config.routeName === routeName
    );
    return !entry || canAccessReport(entry[0], ctx);
}

export function visibleReports(ctx) {
    return Object.entries(REPORT_CONFIG)
        .filter(([type, config]) => config.dashboardSection && canAccessReport(type, ctx))
        .map(([type, config]) => ({ type, ...config }));
}

/**
 * Date-range filtering helpers shared across reports that support hasDateRange.
 */
export function parseDateRange(startStr, endStr) {
    // A bare YYYY-MM-DD string is parsed as UTC by JavaScript. Report inputs
    // represent the tenant/operator's local calendar day, so construct local
    // midnight explicitly to keep this safety filter aligned with the server
    // time_after/time_before bounds.
    const start = startStr ? new Date(`${startStr}T00:00:00`) : null;
    const end = endStr ? new Date(`${endStr}T00:00:00`) : null;
    if (end) end.setHours(23, 59, 59, 999);
    return { start, end };
}

export function filterByDateRange(items, getDate, { start, end }) {
    if (!start && !end) return items;
    return items.filter((item) => {
        const d = new Date(getDate(item));
        if (start && d < start) return false;
        if (end && d > end) return false;
        return true;
    });
}
